<script setup>
import {
  ref,
  computed,
  defineAsyncComponent,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import AppHeader from "./AppHeader.vue";
import Sidebar from "./Sidebar.vue";
import AppFooter from "./AppFooter.vue";
import MENU, { translateMenu } from "./helper/menu.js";
import { resetViewToTable } from "@/composables/useTabState.js";
import { useAuthStore } from "@/stores/auth";
/* ===== LocalStorage helpers ===== */
const LS = {
  TABS: "app:openTabs",
  ACTIVE: "app:activeTab",
};
const loadJSON = (k, fb) => {
  try {
    const s = localStorage.getItem(k);
    return s ? JSON.parse(s) : fb;
  } catch {
    return fb;
  }
};
const saveJSON = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};

/* ===== i18n ===== */
const { t } = useI18n();

/* ===== Computed ===== */
const auth = useAuthStore();
const translatedMenu = computed(() => translateMenu(MENU, t, auth.permissions));

const isVendor = computed(
  () => (auth.user?.role_name || "").toLowerCase() === "vendor"
);

// Vendor tidak punya menu Dashboard, jadi landing tab default & tujuan
// "close all tabs" harus jatuh ke menu pertama yang memang tersedia untuknya,
// bukan ke "dashboard" yang di sidebar-nya disembunyikan.
const defaultLandingKey = computed(() => {
  if (!isVendor.value) return "dashboard";
  const firstChild = translatedMenu.value[0]?.children?.[0];
  return firstChild?.key || "dashboard";
});
// const translatedMenu = computed(() => {
//   if (!auth.permissionsReady) return [];
//   console.log("mmmm", auth);

//   return translateMenu(MENU, t, auth.permissions);
// });

/* ===== State ===== */
const selectedKey = ref(defaultLandingKey.value);
const openTabs = ref([{ key: defaultLandingKey.value }]); // Remove static label
const activeTabKey = ref(defaultLandingKey.value);
const isInternalNavigation = ref(false); // Flag untuk membedakan internal navigation vs tab switching
const lastNavigationSource = ref("external"); // 'external' | 'internal' | 'manual'
const newlyOpenedTabKey = ref(null); // string | null

function focusTab(key) {
  // If clicking on already active tab, clean the URL
  if (activeTabKey.value === key && location.hash.includes("?")) {
    location.hash = key;
  }

  activeTabKey.value = key;
  selectedKey.value = key;
}

// Function to set internal navigation flag (called by components)
function setInternalNavigation() {
  lastNavigationSource.value = "internal";
  isInternalNavigation.value = true;
}

// Function to set external navigation flag (called by components)
function setExternalNavigation() {
  lastNavigationSource.value = "external";
  isInternalNavigation.value = false;
}

function onSelect(key) {
  const exists = openTabs.value.find((t) => t.key === key);

  if (!exists) {
    // 🔥 TAB BARU
    openTabs.value.push({ key });
    newlyOpenedTabKey.value = key;
  } else {
    // 🔁 TAB LAMA
    newlyOpenedTabKey.value = null;
  }

  isInternalNavigation.value = false;
  focusTab(key);
}

function closeTab(key) {
  const idx = openTabs.value.findIndex((t) => t.key === key);
  if (idx === -1) return;
  if (openTabs.value.length === 1) return;

  window.dispatchEvent(new CustomEvent("tab-closed", { detail: { key } }));

  // Reset view state to table before closing tab
  resetViewToTable(key);

  openTabs.value.splice(idx, 1);
  if (activeTabKey.value === key) {
    const newIdx = Math.max(0, idx - 1);
    const newTab = openTabs.value[newIdx];
    focusTab(newTab.key);
  }
}

/* ===== labels & view paths ===== */
const labels = computed(() => {
  const map = new Map();
  map.set("dashboard", t("sidebar.dashboard"));
  for (const section of translatedMenu.value) {
    for (const child of section.children || []) {
      map.set(child.key, child.label);
    }
  }
  return map;
});

const icons = (() => {
  const map = new Map();
  map.set("dashboard", "bi-house-door");
  for (const section of MENU) {
    for (const child of section.children || []) {
      map.set(child.key, child.icon);
    }
  }
  return map;
})();

const viewPathByKey = (() => {
  const map = new Map();
  map.set("dashboard", "Dashboard"); // -> /src/views/Dashboard.vue
  for (const section of MENU) {
    for (const child of section.children || []) {
      if (child.view) map.set(child.key, child.view);
    }
  }
  return map;
})();

/* ===== load views via Vite glob ===== */
const viewModules = import.meta.glob("/src/views/**/*.vue");
const asyncCache = new Map();
const FallbackNotFound = { template: "<div>View not found.</div>" };

function getAsyncViewByKey(key) {
  const path = viewPathByKey.get(key);
  if (!path) return FallbackNotFound;
  const file = `/src/views/${path}.vue`;
  const loader = viewModules[file];
  if (!loader) {
    console.warn("[view missing]", file);
    return FallbackNotFound;
  }
  if (!asyncCache.has(key)) {
    asyncCache.set(key, defineAsyncComponent(loader));
  } else {
    // console.log(`♻️ Reusing cached component for: ${key}`)
  }
  return asyncCache.get(key);
}

const currentTitle = computed(
  () => labels.value.get(activeTabKey.value) || "Untitled",
);
const activeView = computed(() => {
  const view = getAsyncViewByKey(activeTabKey.value);
  return view;
});

/* ===== Filter & collapse ===== */
const filter = ref("all");
const collapsed = ref(false);

const filteredMenu = computed(() => {
  const sections = {
    "trip-tariff": ["trip-mgmt", "tarif"],
    report: ["report"],
    container: ["container-mgmt"],
    "master-system": ["master", "system"],
  };
  if (filter.value === "all") return translatedMenu.value;
  const allow = new Set(sections[filter.value] || []);
  return translatedMenu.value.filter((sec) => allow.has(sec.key));
});

const isGlobalRefreshing = ref(false);
let refreshTimeoutId = null;

const handleGlobalRefreshComplete = (event) => {
  const targetKey = event?.detail?.key;
  if (targetKey && targetKey !== activeTabKey.value) {
    return;
  }
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  isGlobalRefreshing.value = false;
};

const triggerGlobalRefresh = () => {
  if (isGlobalRefreshing.value) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("global-refresh", {
      detail: { key: activeTabKey.value },
      cancelable: true,
    }),
  );

  isGlobalRefreshing.value = true;
  refreshTimeoutId = window.setTimeout(() => {
    isGlobalRefreshing.value = false;
    refreshTimeoutId = null;
  }, 5000);
};

/* ===== Sync tab <-> URL hash ===== */
watch(activeTabKey, (k) => {
  if (k) {
    const currentHash = location.hash;
    const { path, params } = parseHashWithParams(currentHash);

    window.dispatchEvent(
      new CustomEvent("tab-activated", {
        detail: {
          key: k,
          isNew: newlyOpenedTabKey.value === k,
        },
      }),
    );

    // Only update hash if the path is different
    if (path !== k) {
      // When switching tabs, clean the URL (remove parameters)
      location.hash = k;
    } else {
      // Path matches, check if we should clean parameters
      if (isInternalNavigation.value) {
        //
      } else {
        location.hash = k;
      }
    }

    // reset setelah dipakai
    newlyOpenedTabKey.value = null;
  }
});

function sanitizeHash(h) {
  return decodeURIComponent(h || "").replace(/^#\/?/, "");
}

function parseHashWithParams(hash) {
  const [path, queryString] = hash.split("?");
  const cleanPath = sanitizeHash(path);
  const params = new URLSearchParams(queryString || "");

  return {
    path: cleanPath,
    params: Object.fromEntries(params.entries()),
  };
}

function ensureOpenAndFocus(key) {
  if (!labels.value.get(key)) return;
  if (!openTabs.value.find((t) => t.key === key)) {
    openTabs.value.push({ key, label: labels.value.get(key) || key });
  }
  focusTab(key);
}

function openTabWithParams(key, params = {}) {
  if (!labels.value.get(key)) return;
  ensureOpenAndFocus(key);
  lastNavigationSource.value = "internal";
  isInternalNavigation.value = true;
  const query = new URLSearchParams(params).toString();
  location.hash = query ? `${key}?${query}` : key;
}

function handleHashChange() {
  const { path, params } = parseHashWithParams(location.hash);

  if (path && labels.value.get(path)) {
    // Determine navigation type
    if (path !== activeTabKey.value) {
      // Different tab - this is external navigation or tab switching
      lastNavigationSource.value = "external";
      isInternalNavigation.value = false;
    } else if (Object.keys(params).length > 0) {
      // Same tab with parameters
      if (lastNavigationSource.value === "internal") {
        isInternalNavigation.value = true;
      } else {
        lastNavigationSource.value = "external";
        isInternalNavigation.value = false;
      }
    } else {
      // Same tab without parameters - manual selection
      lastNavigationSource.value = "manual";
      isInternalNavigation.value = false;
    }

    // Always emit event for URL change, even if tab is already active
    window.dispatchEvent(
      new CustomEvent("url-change", {
        detail: { path, params },
      }),
    );

    // Only open/focus tab if it's different from current active tab
    if (path !== activeTabKey.value) {
      ensureOpenAndFocus(path);
    }
  }
}

/* ===== Persist & Restore (LocalStorage) ===== */
function restoreFromLocalStorage() {
  // 1) restore list tab (sebagai urutan key, lebih tahan perubahan label)
  const keys = loadJSON(LS.TABS, null);
  if (Array.isArray(keys) && keys.length) {
    let valid = keys.filter((k) => k === "dashboard" || labels.value.get(k));
    // Vendor tidak punya tab dashboard, buang sisa tab lama itu dari state.
    if (isVendor.value) valid = valid.filter((k) => k !== "dashboard");
    if (valid.length) {
      // Don't store label, just key - label will be computed reactively
      openTabs.value = valid.map((k) => ({ key: k }));
    }
  }
  // 2) restore active
  const a = loadJSON(LS.ACTIVE, null);
  if (a && labels.value.get(a) && !(isVendor.value && a === "dashboard")) {
    activeTabKey.value = a;
    selectedKey.value = a;
  }
}
watch(
  openTabs,
  (tabs) => {
    saveJSON(
      LS.TABS,
      tabs.map((t) => t.key),
    );
  },
  { deep: true },
);

watch(activeTabKey, (k) => {
  if (k) saveJSON(LS.ACTIVE, k);
});

/* ===== Lifecycle ===== */
onMounted(() => {
  let { path, params } = parseHashWithParams(location.hash);
  window.addEventListener(
    "global-refresh-complete",
    handleGlobalRefreshComplete,
  );

  auth.hydratePermissions();

  // Permissions dicache di localStorage dan hanya di-fetch ulang dari server
  // kalau cache kosong (lihat auth.js) - jadi kalau ada module/menu baru yang
  // permission-nya baru ditambahkan setelah user login, cache lama tidak akan
  // pernah terupdate sampai user logout/login lagi. Refresh di sini supaya
  // menu baru langsung muncul begitu app di-reload, tanpa perlu logout.
  if (auth.isAuthenticated) {
    auth.fetchMyPermissions();
  }

  // Vendor tidak punya menu/tab dashboard, jadi kalau hash URL masih
  // menunjuk ke "dashboard" (sisa sebelum login, atau reset saat logout),
  // arahkan ke default landing key milik vendor.
  if (path === "dashboard" && isVendor.value) {
    path = defaultLandingKey.value;
    params = {};
    location.hash = path;
  }

  if (path && labels.value.get(path)) {
    // On mount, preserve parameters for reload scenarios
    if (Object.keys(params).length > 0) {
      lastNavigationSource.value = "external";
      isInternalNavigation.value = true; // Set to true to preserve parameters on reload
    } else {
      lastNavigationSource.value = "manual";
      isInternalNavigation.value = false;
    }

    // Set activeTabKey first to prevent watch from cleaning URL
    activeTabKey.value = path;
    selectedKey.value = path;

    // Restore from localStorage but don't override URL-based tab
    restoreFromLocalStorage();

    // Ensure tab is open and focused
    ensureOpenAndFocus(path);

    // Emit event untuk component yang membutuhkan URL parameters
    window.dispatchEvent(
      new CustomEvent("url-change", {
        detail: { path, params },
      }),
    );
  } else {
    // No URL path, restore from localStorage
    restoreFromLocalStorage();
    ensureOpenAndFocus(activeTabKey.value);
    if (!location.hash) {
      location.hash = activeTabKey.value;
    }
  }

  window.addEventListener("hashchange", handleHashChange);
});

onBeforeUnmount(() => {
  window.removeEventListener("hashchange", handleHashChange);
  window.removeEventListener(
    "global-refresh-complete",
    handleGlobalRefreshComplete,
  );
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
});

// Expose functions to global scope for components to use
window.setInternalNavigation = setInternalNavigation;
window.setExternalNavigation = setExternalNavigation;
window.openTab = openTabWithParams;

function closeAllTabs(keepKey = defaultLandingKey.value) {
  // Reset view state to table for all tabs before closing
  openTabs.value.forEach((tab) => {
    if (tab.key !== keepKey) {
      window.dispatchEvent(new CustomEvent("tab-closed", { detail: { key: tab.key } }));
      resetViewToTable(tab.key);
    }
  });

  // Don't store label, just key - label will be computed reactively
  openTabs.value = [{ key: keepKey }];
  focusTab(keepKey); // ini akan update activeTabKey, localStorage, dan hash
}
</script>

<template>
  <div class="page">
    <AppHeader />
    <div class="layout" :class="{ collapsed: collapsed }">
      <Sidebar
        :items="filteredMenu"
        :collapsed="collapsed"
        :filter="filter"
        :active-key="activeTabKey"
        @filterChange="(val) => (filter = val)"
        @toggleCollapse="collapsed = !collapsed"
        @select="onSelect"
      />
      <main class="content">
        <div class="tabs">
          <div
            v-for="tab in openTabs"
            :key="tab.key"
            class="tab"
            :class="{ active: tab.key === activeTabKey }"
            @click="focusTab(tab.key)"
            :title="labels.get(tab.key) || tab.key"
          >
            <i :class="icons.get(tab.key) || 'bi-file'" class="tab-icon"></i>
            <span class="tab-title">{{ labels.get(tab.key) || tab.key }}</span>
            <button
              class="tab-close"
              @click.stop="closeTab(tab.key)"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <span class="tabs-spacer"></span>
          <button
            class="icon-btn tabs-action danger"
            @click="closeAllTabs()"
            type="button"
            aria-label="Close all tabs"
            title="Close all tabs"
          >
            <i class="bi bi-x-circle-fill"></i>
          </button>
        </div>
        <div class="page-header">
          <h2 class="page-title text-body">{{ currentTitle }}</h2>
          <button
            class="refresh-btn"
            type="button"
            :disabled="isGlobalRefreshing"
            @click="triggerGlobalRefresh"
            :title="t('general.refresh')"
          >
            <i
              class="bi bi-arrow-clockwise"
              :class="{ spinning: isGlobalRefreshing }"
            ></i>
            <span>{{ t("general.refresh") }}</span>
          </button>
        </div>

        <!-- Kontainer halaman: render komponen view sesuai key -->
        <div class="content-body">
          <Suspense>
            <KeepAlive>
              <component :is="activeView" :key="activeTabKey" />
            </KeepAlive>
            <template #fallback
              ><div class="view-loading">Loading…</div></template
            >
          </Suspense>
        </div>
      </main>
    </div>
    <AppFooter />
  </div>
</template>

<style scoped src="./style/Layout.css"></style>

<style scoped>
/* Ensure components don't reset when switching tabs */
.content-body {
  position: relative;
}

.content-body > * {
  position: relative;
  z-index: 1;
}

/* Prevent component recreation */
.content-body > div {
  will-change: auto;
}
</style>
